import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import PostAddIcon from "@mui/icons-material/PostAdd";
import RemoveIcon from "@mui/icons-material/Remove";
import WarningIcon from "@mui/icons-material/Warning";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { cloneDeep } from "lodash";
import { useEffect, useState } from "react";
import WarningPopup from "../../modal/WarningPopup";
import EditTestDataComponent from "../edit/EditTestDataComponent";
import editTestDataObjectStyles from "../edit/EditTestDataObject.module.css";

interface EditTestDataObjectProps {
  title: string;
  data: any;
  objectIndex: number;
  isExpandAll: boolean;
  expandCollapseKey: number;
  isInsideArray: boolean;
  isNullKeyExists: boolean;
  handleTestDataObjectChange: (changedJson: any) => void;
  handleKeyChange: (newKey: string) => void;
  handleOnRemove: (keyToRemove: string, objectIndex?: number) => void;
  handleOnAddAttribute: (
    isParentArray: boolean,
    fieldIndex: number,
    objectIndex?: number
  ) => void;
  handleOnAddObject: (
    isParentArray: boolean,
    fieldIndex: number,
    objectIndex?: number
  ) => void;
  handleOnArrayCheck: (oldKey: string, isArray: boolean) => void;
}

const EditTestDataObject = ({
  title,
  data,
  objectIndex,
  isExpandAll,
  expandCollapseKey,
  isInsideArray,
  isNullKeyExists,
  handleTestDataObjectChange,
  handleKeyChange,
  handleOnRemove,
  handleOnAddAttribute,
  handleOnAddObject,
  handleOnArrayCheck,
}: EditTestDataObjectProps) => {
  const [objectData, setObjectData] = useState(data);
  const [isExpanded, setIsExpanded] = useState<boolean>(isExpandAll);
  const [isDiscardPopupOpen, setIsDiscardPopupOpen] = useState(false);
  const [isArrayChecked, setIsArrayChecked] = useState(
    Array.isArray(objectData)
  );

  useEffect(() => {
    setIsExpanded(isExpandAll);
  }, [isExpandAll, expandCollapseKey]);

  useEffect(() => {
    if (Object.keys(objectData).length === 0) setIsExpanded(true);
  }, []);

  const handleOnAccordionClick = () => {
    setIsExpanded((prevExpanded) => !prevExpanded);
  };

  const handleOnDiscardPopupClose = () => {
    setIsDiscardPopupOpen(false);
  };

  const handleOnDisCardPopupOk = () => {
    if (!isArrayChecked) {
      // for array
      handleOnArrayCheck(title, true);
    } else {
      // for object
      handleOnArrayCheck(title, false);
    }
    setIsArrayChecked((prevChecked) => !prevChecked);
    setIsDiscardPopupOpen(false);
  };

  const handleOnCheckBoxClick = () => {
    if (Object.keys(objectData).length > 0) {
      setIsDiscardPopupOpen(true);
    } else {
      handleOnDisCardPopupOk();
    }
  };

  useEffect(() => {
    setObjectData(cloneDeep(data));
  }, [data]);

  return (
    <>
      <Accordion
        sx={{
          margin: "8px 0px",
          position: "static",
        }}
        expanded={isExpanded}
        onChange={handleOnAccordionClick}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: "#FE524A30",
            "&.Mui-focusVisible, &.Mui-expanded": {
              backgroundColor: "#FE524 A30",
              height: "3rem",
              minHeight: "3rem",
            },
            ".MuiAccordionSummary-content": {
              display: "flex",
              justifyContent: "space-between",
            },
          }}
        >
          {!isInsideArray ? (
            <TextField
              variant="standard"
              value={title}
              size="small"
              inputProps={{
                style: { fontSize: "0.875rem", fontWeight: "bold" },
              }}
              onChange={(e) => {
                handleKeyChange(e.target.value);
              }}
              onClick={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
            />
          ) : (
            <Box></Box>
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: "2rem",
            }}
          >
            {!isInsideArray && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Checkbox
                  sx={{ "&.MuiCheckbox-root": { padding: "0px" } }}
                  checked={isArrayChecked}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOnCheckBoxClick();
                  }}
                />
                <Typography variant="subtitle1">IsArray</Typography>
              </Box>
            )}

            <Box sx={{ display: "flex", gap: "0.5rem" }}>
              <Tooltip
                placement="top-start"
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: "offset",
                        options: {
                          offset: [-15, -10],
                        },
                      },
                    ],
                  },
                }}
                title="Remove"
              >
                <span>
                  <IconButton
                    sx={{ padding: "4px", cursor: "pointer" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOnRemove(title, objectIndex);
                    }}
                  >
                    <RemoveIcon />
                  </IconButton>
                </span>
              </Tooltip>

              {!isInsideArray && (
                <Tooltip
                  placement="top-start"
                  slotProps={{
                    popper: {
                      modifiers: [
                        {
                          name: "offset",
                          options: {
                            offset: [-15, -10],
                          },
                        },
                      ],
                    },
                  }}
                  title={isNullKeyExists ? "" : "Add Attribute"}
                >
                  <span>
                    <IconButton
                      sx={{ padding: "4px", cursor: "pointer" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOnAddAttribute(isInsideArray, objectIndex);
                      }}
                      disabled={isNullKeyExists}
                    >
                      <PlaylistAddIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}

              <Tooltip
                placement="top-start"
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: "offset",
                        options: {
                          offset: [-15, -10],
                        },
                      },
                    ],
                  },
                }}
                title={isNullKeyExists ? "" : "Add Object"}
              >
                <span>
                  <IconButton
                    sx={{ padding: "4px", cursor: "pointer" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOnAddObject(isInsideArray, objectIndex);
                    }}
                    disabled={isNullKeyExists}
                  >
                    <PostAddIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ backgroundColor: "white", padding: "1%" }}>
          {Object.keys(objectData).length > 0 ? (
            <EditTestDataComponent
              isExpandAll={isExpandAll}
              expandCollapseKey={expandCollapseKey}
              innerJson={objectData}
              handleInnerJsonChange={(changedJson: any) => {
                handleTestDataObjectChange(cloneDeep(changedJson));
              }}
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "0.5rem",
              }}
            >
              {!Array.isArray(objectData) && (
                <Tooltip
                  placement="top-start"
                  slotProps={{
                    popper: {
                      modifiers: [
                        {
                          name: "offset",
                          options: {
                            offset: [-15, -10],
                          },
                        },
                      ],
                    },
                  }}
                  title="Add Attribute"
                >
                  <span>
                    <IconButton
                      sx={{ padding: "4px", cursor: "pointer" }}
                      onClick={() => {
                        handleOnAddAttribute(
                          Array.isArray(objectData),
                          0,
                          objectIndex
                        );
                      }}
                    >
                      <PlaylistAddIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip
                placement="top-start"
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: "offset",
                        options: {
                          offset: [10, -10],
                        },
                      },
                    ],
                  },
                }}
                title="Add Object"
              >
                <span>
                  <IconButton
                    sx={{ padding: "4px", cursor: "pointer" }}
                    onClick={() =>
                      handleOnAddObject(
                        Array.isArray(objectData),
                        0,
                        objectIndex
                      )
                    }
                  >
                    <PostAddIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
      {isDiscardPopupOpen && (
        <WarningPopup
          isOpen={true}
          onClose={handleOnDiscardPopupClose}
          actionButtons={[
            {
              name: "OK",
              onButtonClick: handleOnDisCardPopupOk,
            },
            {
              name: "Cancel",
              onButtonClick: handleOnDiscardPopupClose,
            },
          ]}
        >
          <Box className={editTestDataObjectStyles.warningPopUpBox}>
            <WarningIcon />
            <Typography variant="body2">
              The conversion from Object to Array (vice-versa) will discard the
              data in it
            </Typography>
          </Box>
        </WarningPopup>
      )}
    </>
  );
};

export default EditTestDataObject;
